import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatDate, formatTime, formatHours, formatCZK, formatCZKCompact, toLocalInputValue } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Download, AlertTriangle, Pencil, Clock, Wallet, Coffee, Hourglass } from "lucide-react";
import { StatCard } from "@/components/shared/PagePrimitives";
import { toast } from "sonner";

// Monthly attendance sheet for a single user (month picker, KPIs, records table,
// CSV export, correction dialog). Reused by /dochazka/vykaz/:userId and the
// employee detail card (Docházka tab). No duplicate logic or endpoints.
export function AttendanceMonthlyView({ userId }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/attendance/monthly?month=${month}&user_id=${userId}`);
    setData(data);
  }, [month, userId]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const res = await api.get(`/attendance/monthly/export?month=${month}&user_id=${userId}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `dochazka_${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const saveCorrection = async () => {
    await api.patch(`/attendance/${edit.id}`, { check_in: edit.check_in, check_out: edit.check_out, break_minutes: edit.break_minutes === "" ? null : Number(edit.break_minutes), note: edit.note });
    toast.success("Záznam opraven."); setEdit(null); load();
  };

  if (!data) return <div className="text-muted-foreground">Načítám…</div>;

  return (
    <div className="space-y-4" data-testid="attendance-monthly-view">
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-11 w-full sm:w-40 min-w-0" data-testid="month-picker" />
        <Button onClick={exportCsv} variant="outline" data-testid="export-csv-btn" className="h-11 w-full sm:w-auto"><Download size={16} /> CSV</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Clock} label="Čisté hodiny" value={formatHours(data.total_hours)} testid="stat-net-hours" />
        <StatCard icon={Hourglass} label="Hrubé hodiny" value={formatHours(data.total_gross_hours)} testid="stat-gross-hours" />
        <StatCard icon={Coffee} label="Odečteno přestávek" value={formatHours(data.total_break_hours)} testid="stat-break-hours" />
        {data.labor_cost != null && (
          <StatCard icon={Wallet} label="Mzdové náklady" value={formatCZKCompact(data.labor_cost)} title={formatCZK(data.labor_cost)} testid="stat-labor-cost" />
        )}
      </div>
      <p className="text-xs text-muted-foreground -mt-1">Náklady se počítají sazbou platnou v den směny (z čistých hodin).</p>

      <div className="bg-card border border-border rounded-[20px] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b border-border">
            <tr>
              <th className="p-3 font-bold">Datum</th><th className="p-3 font-bold">Příchod</th><th className="p-3 font-bold">Odchod</th>
              <th className="p-3 font-bold">Stavba</th><th className="p-3 font-bold">Hodiny</th><th className="p-3 font-bold">Report</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.rows.map((r) => (
              <tr key={r.id} data-testid={`sheet-row-${r.id}`}>
                <td className="p-3 whitespace-nowrap">{formatDate(r.date)}</td>
                <td className="p-3">{formatTime(r.check_in)}</td>
                <td className="p-3">{r.check_out ? formatTime(r.check_out) : "—"}</td>
                <td className="p-3">{r.project} {!r.has_gps && <AlertTriangle size={13} className="inline text-amber-500 ml-1" />}
                  {r.corrections?.length > 0 && <span className="text-xs text-muted-foreground ml-1">(opraveno)</span>}
                </td>
                <td className="p-3 font-semibold">{formatHours(r.hours)}
                  {r.break_minutes != null && r.break_minutes > 0 && <div className="text-xs font-normal text-muted-foreground inline-flex items-center gap-1"><Coffee size={11} /> {r.break_minutes} min</div>}
                </td>
                <td className="p-3 text-muted-foreground max-w-xs">{r.report || <span className="italic opacity-60">—</span>}</td>
                <td className="p-3 text-right">
                  <Button variant="ghost" size="icon" onClick={() => setEdit({ id: r.id, check_in: toLocalInputValue(r.check_in), check_out: toLocalInputValue(r.check_out), break_minutes: r.break_minutes ?? "", note: "" })} data-testid={`edit-${r.id}`}>
                    <Pencil size={15} />
                  </Button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Žádné záznamy v tomto měsíci.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Oprava záznamu</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Příchod</Label><Input type="datetime-local" value={edit.check_in || ""} onChange={(e) => setEdit({ ...edit, check_in: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="edit-checkin" /></div>
              <div><Label>Odchod</Label><Input type="datetime-local" value={edit.check_out || ""} onChange={(e) => setEdit({ ...edit, check_out: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="edit-checkout" /></div>
              <div><Label>Přestávka (min)</Label><Input type="number" min={0} max={720} value={edit.break_minutes} onChange={(e) => setEdit({ ...edit, break_minutes: e.target.value })} placeholder="auto (30 při >6 h)" className="h-11 mt-1" data-testid="edit-break" /></div>
              <div><Label>Důvod opravy</Label><Input value={edit.note || ""} onChange={(e) => setEdit({ ...edit, note: e.target.value })} className="h-11 mt-1" data-testid="edit-note" /></div>
              <Button onClick={saveCorrection} className="w-full h-11 font-bold" data-testid="save-correction">Uložit opravu</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
